/**
 * Clients → Engagement — prototype `VIEWS.engage` (docs/prototype.html 3560–3596): head action "Add what is due
 * to the task list", four stats, Grid / Next 14 days with the person filter (managers), the client × eight
 * activities grid (cell = state colour, label, "Done Nd ago, booked …"), and the cell pop-up (`engDo`): Done on,
 * note (required for MIS and stakeholder update), "Book a date instead", "Mark done". Guide §5.3: an overdue
 * cell's pop-up can also send a WhatsApp reminder (wa.me) to the client's SPOC.
 *
 * Data: GET /engagement/grid (managers all clients, a member their own); POST /clients/{id}/engagement/{key}/log;
 * POST …/book; POST /clients/{id}/engagement/tasks (per client — the head button runs it for every row with
 * something due). Every state, label and sub-label is the server's; nothing is recomputed here.
 */
import { useMemo, useState } from 'react';
import {
  ENGAGEMENT_NOTE_RULE,
  engagementNoteMissing,
  type ActivityKey,
  type ClientEngagement,
  type EngagementActivity,
  type EngagementCell,
} from '@amplispot/contracts';
import { addEngagementTasks, overdueTone, refreshEngagement, STATE_CLS, useBookEngagement, useEngagementGrid, useLogEngagement, waLink } from '../../lib/engagement';
import { useUsers } from '../../lib/data';
import { dayLabel, firstName } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Lbl } from '../../shell/Help';
import { addDays, daysBetween } from '../../lib/work';
import { Empty, ErrorState, Field, Loading, Modal, Pills, errorText, useToast } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';
import { ClientLogo } from './parts';
import { useQueryClient } from '@tanstack/react-query';
import { beatOf } from '@amplispot/domain';

type View = 'grid' | 'agenda';
type Open = { row: ClientEngagement; cell: EngagementCell };

/** Prototype `ENGA[].ph`: what to write in the note for each activity. */
const NOTE_HINT: Record<ActivityKey, string> = {
  wa: 'What did you send or answer in the group?',
  meet: 'Who attended, online or in person',
  train: 'Who was trained, on what',
  diy: 'Which video, and where it was shared',
  testi: 'Who recorded it',
  theme: 'Name the theme or colours used this time',
  mis: 'What we suggested to lift usage or quality',
  stake: 'What improved because of the project, and where we can improve together',
};

/** This client's beat: a new client's shorter training cadence reads "every 15 days (new client)" (prototype ENGA.train). */
const cellBeat = (row: ClientEngagement, cell: EngagementCell, act: EngagementActivity | undefined) =>
  row.is_new_client && act?.new_client_cadence_days != null && cell.cadence_days === act.new_client_cadence_days
    ? `${beatOf(cell.activity_key, cell.cadence_days)} (new client)`
    : act?.beat ?? beatOf(cell.activity_key, cell.cadence_days);

/** The column head's beat. An activity with its own new-client cadence (training) differs per client, so the head says
 *  only the established rhythm in a word, "monthly" — the prototype prints 'monthly' for a per-client beat (ENGA.train). */
const headBeat = (a: EngagementActivity) =>
  a.new_client_cadence_days != null && a.new_client_cadence_days !== a.cadence_days && a.cadence_days === 30 ? 'monthly' : a.beat;

export function Engagement() {
  const { me, can } = useSession();
  const mgr = can('clients.read.all');
  const [who, setWho] = useState('');
  const [view, setView] = useState<View>('grid');
  const grid = useEngagementGrid(mgr && who ? { spoc_id: who } : {});
  const users = useUsers(can('users.read'));
  const [open, setOpen] = useState<Open | null>(null);
  const [adding, setAdding] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  // Anyone who can be a SPOC: associate directors first, then members (as Team → Plan lists them).
  const people = useMemo(
    () =>
      (users.data ?? [])
        .filter((u) => u.status === 'active' && (u.role === 'ops_ad' || u.role === 'ops_member'))
        .sort((a, b) => Number(a.role !== 'ops_ad') - Number(b.role !== 'ops_ad')),
    [users.data],
  );

  if (grid.isLoading) return <Loading />;
  if (grid.error) return <ErrorState error={grid.error} onRetry={() => void grid.refetch()} />;
  const g = grid.data!;
  const acts = new Map(g.activities.map((a) => [a.key, a]));

  const addDue = async () => {
    const rows = g.rows.filter((r) => r.summary.overdue + r.summary.due_soon > 0);
    if (!rows.length) return toast('Everything due already has a task');
    setAdding(true);
    let n = 0;
    const failed: string[] = [];
    for (const r of rows) {
      try {
        n += (await addEngagementTasks(r.client.id)).created.length;
      } catch {
        failed.push(r.client.name);
      }
    }
    setAdding(false);
    refreshEngagement(qc);
    toast(
      (n ? `${n} task${n === 1 ? '' : 's'} added for the SPOCs` : 'Everything due already has a task') +
        (failed.length ? `. Could not add for ${failed.join(', ')}; try again.` : ''),
    );
  };

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn pri" onClick={() => void addDue()} disabled={adding || !g.rows.length}>
            {adding ? 'Adding…' : 'Add what is due to the task list'}
          </button>
        </div>
      </div>
      <div className="stats">
        <Stat k="On track" v={`${g.summary.on_track_pct}%`} s={`${g.summary.on_track} of ${g.summary.total} activities`} />
        <Stat k="Overdue" v={g.summary.overdue} s="missed their beat" cls={g.summary.overdue ? 'red' : 'green'} />
        <Stat k="Due today" v={g.summary.due_today} cls={g.summary.due_today ? 'amber' : ''} />
        <Stat k="Clients" v={g.summary.clients} s={mgr ? 'across the team' : 'yours'} />
      </div>
      <div className="bar">
        <Pills<View>
          options={[
            ['grid', 'Grid'],
            ['agenda', 'Next 14 days'],
          ]}
          value={view}
          onChange={setView}
        />
        <div className="grow" />
        {mgr ? (
          <select className="in" style={{ width: 'auto' }} value={who} onChange={(e) => setWho(e.target.value)} aria-label="Whose clients">
            <option value="">Everyone</option>
            {people.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      {!g.rows.length ? (
        <div className="panel">
          <Empty title={mgr && who ? 'No clients for this person' : mgr ? 'No clients yet' : 'No clients of yours yet'}>
            {mgr ? 'Clients appear here once they are in the client book with a SPOC.' : 'Clients appear here once you are their SPOC.'}
          </Empty>
        </div>
      ) : view === 'grid' ? (
        <div className="panel" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th>Client</th>
                {g.activities.map((a) => (
                  <th key={a.key} style={{ minWidth: 104 }}>
                    {a.name}
                    <div className="sub" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                      {headBeat(a)}
                    </div>
                  </th>
                ))}
                <th className="tr"><Lbl>On track</Lbl></th>
              </tr>
            </thead>
            <tbody>
              {g.rows.map((r) => (
                <tr key={r.client.id}>
                  <td style={{ minWidth: 170 }}>
                    <div className="split">
                      <ClientLogo id={r.client.id} name={r.client.name} size={30} />
                      <div>
                        <b>{r.client.name}</b>
                        {r.is_new_client ? (
                          <>
                            {' '}
                            <span className="chip info">New client</span>
                          </>
                        ) : null}
                        <div className="sub">SPOC {firstName(r.client.spoc.name)}</div>
                      </div>
                    </div>
                  </td>
                  {g.activities.map((a) => {
                    const c = r.cells.find((x) => x.activity_key === a.key);
                    return (
                      <td key={a.key} style={{ padding: '8px 6px' }}>
                        {c ? (
                          <button className={`engc ${STATE_CLS[c.state]}`} onClick={() => setOpen({ row: r, cell: c })} title={c.last_note ?? undefined}>
                            <b>{c.label}</b>
                            <small>{c.sub_label}</small>
                          </button>
                        ) : (
                          <span className="sub">-</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="tr">
                    <span className={`chip ${overdueTone(r.summary.overdue)}`}>
                      {r.summary.on_track} of {r.summary.total}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Agenda rows={g.rows} asOf={g.as_of} acts={acts} onOpen={setOpen} />
      )}
      {open ? <CellModal row={open.row} cell={open.cell} act={acts.get(open.cell.activity_key)} asOf={g.as_of} meId={me.user.id} mgr={mgr} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

const AGENDA_PAGE = 40;

/** Prototype `engAgenda`: what is overdue, then each of the next 14 days. */
function Agenda({ rows, asOf, acts, onOpen }: { rows: ClientEngagement[]; asOf: string; acts: Map<ActivityKey, EngagementActivity>; onOpen: (o: Open) => void }) {
  // Each day group lists AGENDA_PAGE rows, then "Show N more": at go-live every never-done cell is overdue.
  const [shown, setShown] = useState<Record<string, number>>({});
  const items = rows.flatMap((row) => row.cells.map((cell) => ({ row, cell, d: cell.days ?? -1 }))).filter((i) => i.d <= 13);
  const groups: [string, typeof items][] = [];
  const over = items.filter((i) => i.cell.state === 'overdue' || i.d < 0).sort((a, b) => a.d - b.d);
  if (over.length) groups.push(['Overdue', over]);
  for (let o = 0; o < 14; o++) {
    const g = items.filter((i) => i.d === o && i.cell.state !== 'overdue');
    if (g.length) groups.push([`${o === 0 ? 'Today, ' : o === 1 ? 'Tomorrow, ' : ''}${dayLabel(addDays(asOf, o))}`, g]);
  }
  if (!groups.length)
    return (
      <div className="empty">
        <b>Nothing due in the next 14 days</b>
      </div>
    );
  return (
    <>
      {groups.map(([t, g]) => (
        <div key={t} className="panel" style={{ marginBottom: 12 }}>
          <div className="hd">
            <h3>{t}</h3>
            <div className="r">
              <span className="sub">{g.length}</span>
            </div>
          </div>
          {g.slice(0, shown[t] ?? AGENDA_PAGE).map((i) => (
            <div key={`${i.row.client.id}-${i.cell.activity_key}`} className="li click" onClick={() => onOpen({ row: i.row, cell: i.cell })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onOpen({ row: i.row, cell: i.cell })}>
              <div className="split" style={{ gap: 10 }}>
                <ClientLogo id={i.row.client.id} name={i.row.client.name} size={28} />
                <div className="b">
                  <div className="t">
                    {i.cell.name}, {i.row.client.name}
                  </div>
                  <div className="m">
                    <span>{firstName(i.row.client.spoc.name)}</span>
                    <span>{cellBeat(i.row, i.cell, acts.get(i.cell.activity_key))}</span>
                  </div>
                </div>
              </div>
              <span className={`chip ${STATE_CLS[i.cell.state]}`}>{i.cell.state === 'overdue' ? i.cell.label : i.d === 0 ? 'Today' : dayLabel(addDays(asOf, i.d))}</span>
            </div>
          ))}
          {g.length > (shown[t] ?? AGENDA_PAGE) ? (
            <div className="li" style={{ justifyContent: 'center' }}>
              <button className="btn sm ghost" onClick={() => setShown((s) => ({ ...s, [t]: (s[t] ?? AGENDA_PAGE) + AGENDA_PAGE }))}>
                Show {Math.min(AGENDA_PAGE, g.length - (shown[t] ?? AGENDA_PAGE))} more of {g.length - (shown[t] ?? AGENDA_PAGE)}
              </button>
            </div>
          ) : null}
        </div>
      ))}
    </>
  );
}

const DONE_OFFSETS = [0, -1, -2, -3, -5, -7];
const BOOK_OFFSETS = [0, 1, 2, 3, 4, 7, 10, 14];
const offLabel = (asOf: string, o: number) => (o === 0 ? 'Today' : o === -1 ? 'Yesterday' : o === 1 ? 'Tomorrow' : dayLabel(addDays(asOf, o)));

/** Prototype `engDo` / `engBook`: record the activity, or book a later date. */
function CellModal(props: { row: ClientEngagement; cell: EngagementCell; act: EngagementActivity | undefined; asOf: string; meId: string; mgr: boolean; onClose: () => void }) {
  const { row, cell, act, asOf } = props;
  const c = row.client;
  const k = cell.activity_key;
  const can = props.mgr || c.spoc.id === props.meId;
  const log = useLogEngagement();
  const book = useBookEngagement();
  const toast = useToast();
  const [mode, setMode] = useState<'log' | 'book'>('log');
  const [off, setOff] = useState(0);
  const [bookOff, setBookOff] = useState(0);
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const rule = ENGAGEMENT_NOTE_RULE[k];
  const ago = cell.last_done_on ? daysBetween(cell.last_done_on, asOf) : null;
  const overdueBy = cell.state === 'overdue' ? (cell.days == null ? null : -cell.days) : null;
  const beat = cellBeat(row, cell, act);

  // Guide §5.3: an overdue cell can send the SPOC a WhatsApp reminder (only when the server shows their phone).
  const remindText = `Hi ${firstName(c.spoc.name)}, ${cell.name.toLowerCase()} for ${c.name} is ${overdueBy ? `overdue by ${overdueBy} day${overdueBy === 1 ? '' : 's'}` : cell.last_done_on ? 'overdue' : 'not done yet'}. Could you do it today, or book a date in the engagement calendar?`;
  const wa = cell.state === 'overdue' && c.spoc.id !== props.meId ? waLink(c.spoc.phone, remindText) : null;

  const save = () => {
    if (engagementNoteMissing(k, note)) {
      setErr(rule?.missing ?? 'Add a note');
      return toast(rule?.missing ?? 'Add a note');
    }
    log.mutate(
      { clientId: c.id, key: k, body: { done_on: addDays(asOf, off), note: note.trim() || undefined, via: 'in_app' } },
      {
        onSuccess: () => {
          toast(`Recorded for ${c.name}`);
          props.onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  };
  const doBook = (date: string | null) =>
    book.mutate(
      { clientId: c.id, key: k, booked_for: date },
      {
        onSuccess: () => {
          toast(date ? 'Booked' : 'Booking cleared');
          props.onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );

  if (mode === 'book')
    return (
      <Modal
        title="Book it"
        onClose={props.onClose}
        footer={
          <>
            {cell.booked_for ? (
              <button className="btn ghost" onClick={() => doBook(null)} disabled={book.isPending}>
                Clear the booking
              </button>
            ) : null}
            <div className="grow" />
            <button className="btn" onClick={props.onClose}>
              Cancel
            </button>
            <button className="btn pri" onClick={() => doBook(addDays(asOf, bookOff))} disabled={book.isPending}>
              Book
            </button>
          </>
        }
      >
        <Field label="Date">
          <select className="in" value={bookOff} onChange={(e) => setBookOff(Number(e.target.value))}>
            {BOOK_OFFSETS.map((o) => (
              <option key={o} value={o}>
                {offLabel(asOf, o)}
              </option>
            ))}
          </select>
        </Field>
      </Modal>
    );

  return (
    <Modal
      title={`${cell.name}: ${c.name}`}
      onClose={props.onClose}
      footer={
        can ? (
          <>
            <button className="btn" onClick={() => setMode('book')}>
              Book a date instead
            </button>
            {wa ? (
              <a className="btn" href={wa} target="_blank" rel="noreferrer">
                Send {firstName(c.spoc.name)} a WhatsApp reminder
              </a>
            ) : null}
            <div className="grow" />
            <button className="btn" onClick={props.onClose}>
              Close
            </button>
            <button className="btn good" onClick={save} disabled={log.isPending}>
              Mark done
            </button>
          </>
        ) : (
          <>
            <div className="grow" />
            <button className="btn" onClick={props.onClose}>
              Close
            </button>
          </>
        )
      }
    >
      <p style={{ marginTop: 0 }}>
        {`${beat.charAt(0).toUpperCase()}${beat.slice(1)}. `}
        {ago == null ? 'Not done yet.' : `Last done ${ago === 0 ? 'today' : `${ago} days ago`}${cell.last_note ? `: ${cell.last_note}` : ''}.`}{' '}
        {cell.state === 'overdue' ? (
          <b style={{ color: 'var(--cold)' }}>{overdueBy ? `Overdue by ${overdueBy} day${overdueBy === 1 ? '' : 's'}.` : 'Overdue.'}</b>
        ) : cell.next_due_on ? (
          `Next due ${cell.days === 0 ? 'today' : dayLabel(cell.next_due_on)}.`
        ) : null}
      </p>
      {can ? (
        <>
          <Field label="Done on">
            <select className="in" value={off} onChange={(e) => setOff(Number(e.target.value))}>
              {DONE_OFFSETS.map((o) => (
                <option key={o} value={o}>
                  {offLabel(asOf, o)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={rule ? rule.label : 'Note (optional)'} error={err ?? undefined}>
            <textarea
              className="in"
              rows={2}
              placeholder={NOTE_HINT[k]}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setErr(null);
              }}
            />
          </Field>
        </>
      ) : null}
    </Modal>
  );
}
